/**
 * Warehouse Storage Hooks
 *
 * React Query hooks for warehouse storage account management.
 * Part of Phase 8: Storage Mapping extension for warehouses.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as warehouseStorageApi from '../api/warehouseStorage';

// Re-export types
export type {
  WarehouseStorageMapping,
  AddStorageAccountInput,
  UpdateStorageMappingInput,
  StorageAccountPurpose,
  StorageMappingStatus,
} from '../api/warehouseStorage';

// Query keys
export const warehouseStorageKeys = {
  all: ['warehouse-storage'] as const,
  lists: () => [...warehouseStorageKeys.all, 'list'] as const,
  list: (warehouseId: string) => [...warehouseStorageKeys.lists(), warehouseId] as const,
  primary: (warehouseId: string) => [...warehouseStorageKeys.all, 'primary', warehouseId] as const,
  detail: (warehouseId: string, mappingId: string) =>
    [...warehouseStorageKeys.all, 'detail', warehouseId, mappingId] as const,
};

/**
 * Hook to fetch storage accounts for a warehouse
 */
export function useWarehouseStorage(warehouseId: string) {
  return useQuery({
    queryKey: warehouseStorageKeys.list(warehouseId),
    queryFn: () => warehouseStorageApi.getStorageAccountsForWarehouse(warehouseId),
    enabled: !!warehouseId,
    staleTime: 30_000, // 30 seconds
  });
}

/**
 * Hook to fetch the primary storage account for a warehouse
 */
export function usePrimaryWarehouseStorage(warehouseId: string) {
  return useQuery({
    queryKey: warehouseStorageKeys.primary(warehouseId),
    queryFn: () => warehouseStorageApi.getPrimaryStorageAccount(warehouseId),
    enabled: !!warehouseId,
    staleTime: 30_000,
  });
}

/**
 * Hook to add a storage account to a warehouse
 */
export function useAddWarehouseStorageAccount(warehouseId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: warehouseStorageApi.AddStorageAccountInput) =>
      warehouseStorageApi.addStorageAccount(warehouseId, input),
    onSuccess: () => {
      // Invalidate list and primary queries
      queryClient.invalidateQueries({ queryKey: warehouseStorageKeys.list(warehouseId) });
      queryClient.invalidateQueries({ queryKey: warehouseStorageKeys.primary(warehouseId) });
    },
  });
}

/**
 * Hook to update a storage account mapping
 */
export function useUpdateWarehouseStorageMapping(warehouseId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      mappingId,
      input,
    }: {
      mappingId: string;
      input: warehouseStorageApi.UpdateStorageMappingInput;
    }) => warehouseStorageApi.updateStorageMapping(warehouseId, mappingId, input),
    onSuccess: () => {
      // Invalidate list and primary queries
      queryClient.invalidateQueries({ queryKey: warehouseStorageKeys.list(warehouseId) });
      queryClient.invalidateQueries({ queryKey: warehouseStorageKeys.primary(warehouseId) });
    },
  });
}

/**
 * Hook to remove a storage account from a warehouse
 */
export function useRemoveWarehouseStorageAccount(warehouseId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ mappingId, force = false }: { mappingId: string; force?: boolean }) =>
      warehouseStorageApi.removeStorageAccount(warehouseId, mappingId, force),
    onSuccess: () => {
      // Invalidate list and primary queries
      queryClient.invalidateQueries({ queryKey: warehouseStorageKeys.list(warehouseId) });
      queryClient.invalidateQueries({ queryKey: warehouseStorageKeys.primary(warehouseId) });
    },
  });
}

/**
 * Hook to set a storage account as primary
 */
export function useSetWarehouseStorageAsPrimary(warehouseId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (mappingId: string) => warehouseStorageApi.setAsPrimary(warehouseId, mappingId),
    onSuccess: () => {
      // Invalidate list and primary queries
      queryClient.invalidateQueries({ queryKey: warehouseStorageKeys.list(warehouseId) });
      queryClient.invalidateQueries({ queryKey: warehouseStorageKeys.primary(warehouseId) });
    },
  });
}

/**
 * Hook to change the purpose of a storage account mapping
 */
export function useChangeWarehouseStoragePurpose(warehouseId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      mappingId,
      purpose,
    }: {
      mappingId: string;
      purpose: warehouseStorageApi.StorageAccountPurpose;
    }) => warehouseStorageApi.changePurpose(warehouseId, mappingId, purpose),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: warehouseStorageKeys.list(warehouseId) });
    },
  });
}

/**
 * Hook to deactivate a storage account mapping
 */
export function useDeactivateWarehouseStorageMapping(warehouseId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (mappingId: string) =>
      warehouseStorageApi.deactivateMapping(warehouseId, mappingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: warehouseStorageKeys.list(warehouseId) });
    },
  });
}

/**
 * Hook to reactivate a storage account mapping
 */
export function useReactivateWarehouseStorageMapping(warehouseId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (mappingId: string) =>
      warehouseStorageApi.reactivateMapping(warehouseId, mappingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: warehouseStorageKeys.list(warehouseId) });
    },
  });
}
